import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { brandingApi } from '../lib/api';
import { isCustomDomain, getCurrentHostname } from '../utils/siteDetection';

interface BrandingContextType {
  logoUrl: string | null;
  accentColor: string;
  companyName: string | null;
  isCustomDomainSite: boolean;
  isLoaded: boolean;
}

const defaultBranding: BrandingContextType = {
  logoUrl: null,
  accentColor: '#2563eb',
  companyName: null,
  isCustomDomainSite: false,
  isLoaded: false,
};

const BrandingContext = createContext<BrandingContextType>(defaultBranding);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [branding, setBranding] = useState<BrandingContextType>(defaultBranding);

  useEffect(() => {
    const loadBranding = async () => {
      const customDomain = isCustomDomain();

      if (customDomain) {
        // On a custom domain - resolve branding from hostname
        try {
          const hostname = getCurrentHostname();
          const resolved = await brandingApi.resolve(hostname);
          setBranding({
            logoUrl: resolved.logoUrl,
            accentColor: resolved.accentColor || '#2563eb',
            companyName: resolved.companyName,
            isCustomDomainSite: true,
            isLoaded: true,
          });
        } catch {
          setBranding({ ...defaultBranding, isCustomDomainSite: true, isLoaded: true });
        }
      } else if (isAuthenticated && user) {
        // On main site, authenticated - use user's branding
        setBranding({
          logoUrl: user.logoUrl || null,
          accentColor: user.accentColor || '#2563eb',
          companyName: user.companyName,
          isCustomDomainSite: false,
          isLoaded: true,
        });
      } else {
        // Main site, not authenticated - use defaults
        setBranding({ ...defaultBranding, isLoaded: true });
      }
    };

    loadBranding();
  }, [user, isAuthenticated]);

  // Set CSS custom property for accent color
  useEffect(() => {
    if (branding.isLoaded) {
      document.documentElement.style.setProperty('--accent-color', branding.accentColor);
      // Light variant for backgrounds (10% opacity)
      document.documentElement.style.setProperty('--accent-color-light', branding.accentColor + '1a');
    }
  }, [branding.accentColor, branding.isLoaded]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
